import React from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilValue } from 'recoil';
import { Box, Button, DialogActions, DialogContent, Grid, TextField } from '@mui/material';
import { ModalActionProps } from 'models/AutoModals';
import { CompanyModel, CompanyType } from 'models/CompanyModel';
import auth from 'recoil/atoms/auth';


type CompanyProps = {
    company: CompanyModel,
    action: string,
} & ModalActionProps<CompanyModel>

const CompanyDialog = ({company, action, onFinish}: CompanyProps) => {
    const my = useRecoilValue(auth);
    const {t} = useTranslation();

    const submit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        const data = Object.fromEntries(new FormData(event.currentTarget).entries()) as unknown as CompanyModel;

        onFinish({
            ...company,
            ...data,
        })
    };

    const nameFieldWidth = company.type === CompanyType.Shipper ?
        (my.type === CompanyType.Master ? 4 : 12) :
        6;

    return (
        <Box component="form" onSubmit={submit}>
            <DialogContent>
                <Grid container spacing={2} mb={2}>
                    <Grid item xs={12} sm={nameFieldWidth}>
                        <TextField
                            name="name"
                            required
                            fullWidth
                            id="name"
                            label={t('name')}
                            autoFocus
                            defaultValue={company.name || ''}
                        />
                    </Grid>

                    {my.type === CompanyType.Master && company.type === CompanyType.Shipper && (
                        <Grid item xs={12} sm={8}>
                            <TextField
                                name="alias"
                                fullWidth
                                id="alias"
                                label={t('alias')}
                                defaultValue={company.alias || ''}
                            />
                        </Grid>
                    )}

                    {company.type === CompanyType.Shipper && (
                        <>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    required
                                    fullWidth
                                    id="exporterCode"
                                    label={t('exporter_code')}
                                    name="exporterCode"
                                    defaultValue={company.exporterCode || ''}
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    fullWidth
                                    id="shipperCode"
                                    label={t('shipper_code')}
                                    name="shipperCode"
                                    defaultValue={company.shipperCode || ''}
                                />
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <TextField
                                    fullWidth
                                    id="qisNumber"
                                    label={t('qis_number')}
                                    name="qisNumber"
                                    defaultValue={company.qisNumber || ''}
                                />
                            </Grid>
                        </>
                    )}

                    <Box width="100%"/>

                    <Grid item xs={12} sm={4}>
                        <TextField
                            required={company.type === CompanyType.Shipper}
                            type="email"
                            fullWidth
                            id="email"
                            label={t('email_address')}
                            name="email"
                            defaultValue={company.email || ''}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            required={company.type === CompanyType.Shipper}
                            fullWidth
                            name="phone"
                            label={t('phone')}
                            type="tel"
                            id="phone"
                            defaultValue={company.phone || ''}
                        />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <TextField
                            fullWidth
                            name="fax"
                            label={t('fax')}
                            type="tel"
                            id="fax"
                            defaultValue={company.fax || ''}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <TextField
                            required={company.type === CompanyType.Shipper}
                            fullWidth
                            name="city"
                            label={t('prefecture_and_city')}
                            id="city"
                            defaultValue={company.city || ''}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            required={company.type === CompanyType.Shipper}
                            fullWidth
                            name="zipCode"
                            label={t('zip_code')}
                            id="zipCode"
                            defaultValue={company.zipCode || ''}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            required={company.type === CompanyType.Shipper}
                            fullWidth
                            name="address"
                            label={t('address')}
                            id="address"
                            defaultValue={company.address || ''}
                        />
                    </Grid>

                    {company.type === CompanyType.Shipper && (
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                name="addressJa"
                                label={t('address_ja')}
                                id="addressJa"
                                defaultValue={company.addressJa || ''}
                            />
                        </Grid>
                    )}
                </Grid>
            </DialogContent>

            <DialogActions>
                <Button type="submit">{action}</Button>
            </DialogActions>
        </Box>
    );
};

export default CompanyDialog;
